import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { fetchUserByUniqueId } from "../../redux/actions/userActions";
import {
  deleteLoginCookieAndSession,
  getLoginCookieAndSession,
} from "../../utils";
import { USER_JUST_LOGGED_IN } from "../../constants";

import { CenteredLoading } from "../../components/common/CenteredLoading";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { SEO } from "../../components/common/SEO";
import { SnackbarNotify } from "../../components/common/SnackbarNotify";
import { FormActionButton } from "../../components/common/FormActionButton";

import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
  Stack,
  Link,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

const Presell: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { loading, users } = useSelector((state: RootState) => state.users);

  // Control which FAQ panel is open (all closed by default)
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);
  // Must agree to terms before "Start seller setup" is enabled
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleFaqChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedFaq(isExpanded ? panel : false);
    };

  useEffect(() => {
    const profileId = getLoginCookieAndSession();
    if (profileId) {
      dispatch(fetchUserByUniqueId(Number(profileId)));
    } else {
      deleteLoginCookieAndSession();
      navigate("/");
    }
  }, [dispatch, navigate]);

  if (!loading && users && users.length <= 0) {
    deleteLoginCookieAndSession();
    navigate("/");
    return null;
  }

  const handleStartSetup = () => {
    navigate("/seller-setup");
  };

  const handleBrowseInstead = () => {
    navigate("/latest-toys");
  };

  return loading || !users ? (
    <CenteredLoading />
  ) : (
    <>
      <SEO
        title="Start Selling • FiddlePiddle • Toys Marketplace"
        description="Set up your secure seller account on FiddlePiddle so you can start listing toys, get paid through Stripe, and manage your sales in one place."
        keywordsContent="sell toys online, toy marketplace, fiddlepiddle seller setup, stripe payouts"
      />

      <div style={{ position: "relative", zIndex: 100 }}>
        <Breadcrumb
          items={[
            { text: "Home", url: "/" },
            { text: "Start Selling", url: "" },
          ]}
        />
      </div>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        {/* Snackbar for "Signed in successfully" (same behavior as Account page) */}
        <SnackbarNotify
          message="Signed in successfully"
          sessionKey={USER_JUST_LOGGED_IN}
        />

        {/* Small label above hero */}
        <Typography
          variant="overline"
          color="primary.main"
          sx={{ letterSpacing: 1, fontWeight: 600 }}
        >
          One-time seller setup
        </Typography>

        {/* Intro + hero card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            background:
              theme.palette.mode === "light"
                ? "linear-gradient(135deg, #ffffff 0%, #fff7f1 40%, #ffffff 100%)"
                : "rgba(0,0,0,0.4)",
          }}
        >
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            justifyContent="space-between"
            gap={3}
          >
            <Box flex={1}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Ready to start selling on FiddlePiddle?
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Before you can post your first toy for sale, we just need a
                quick one-time setup for your seller account. This keeps your
                payouts secure, helps us prevent fraud, and makes sure buyers
                can trust every seller on FiddlePiddle.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                It only takes a few minutes, and once you&apos;re done, every
                future listing and payout runs on autopilot.
              </Typography>
            </Box>

            {/* Simple video placeholder */}
            <Paper
              elevation={0}
              sx={{
                flexShrink: 0,
                width: isMobile ? "100%" : 260,
                height: 150,
                borderRadius: 3,
                border: "1px dashed",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                background:
                  "radial-gradient(circle at top left, rgba(255,143,66,0.12), transparent 55%), radial-gradient(circle at bottom right, rgba(0,0,0,0.05), transparent 55%)",
              }}
            >
              <Box textAlign="center">
                <PlayCircleOutlineIcon
                  sx={{ fontSize: 42, mb: 1, color: "primary.main" }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Coming soon: 45-second setup walkthrough
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Paper>

        {/* Trust badges */}
        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <LockOutlinedIcon color="primary" />
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Secure payouts via Stripe
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Industry-standard encryption and compliance.
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <VerifiedUserOutlinedIcon color="primary" />
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Verified seller profiles
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Builds trust with collectors and parents.
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <PaymentsOutlinedIcon color="primary" />
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Fast, automatic deposits
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  First payout held for safety, then rolling every 2 days.
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* How it works + requirements */}
        <Grid container spacing={4} mb={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              How this works
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              • You’ll go through a short, guided form powered by Stripe for
              secure payouts.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              • We’ll ask for basic info about you and where you want your money
              sent.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Once you&apos;re approved, you can list toys, get paid, and
              manage everything from your FiddlePiddle account.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                What you&apos;ll need
              </Typography>
              <Stack spacing={1.2}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <CheckCircleOutlineIcon
                    fontSize="small"
                    sx={{ mt: "2px", color: "primary.main" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Your full legal name and residential address
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <CheckCircleOutlineIcon
                    fontSize="small"
                    sx={{ mt: "2px", color: "primary.main" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Bank account or debit card for payouts
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <CheckCircleOutlineIcon
                    fontSize="small"
                    sx={{ mt: "2px", color: "primary.main" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Optional: government ID, if Stripe requests a one-time
                    verification
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Shipping & listing best practices */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor:
              theme.palette.mode === "light"
                ? "rgba(0,0,0,0.015)"
                : "rgba(255,255,255,0.02)",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Shipping & listing best practices
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            paragraph
            sx={{ mb: 2 }}
          >
            FiddlePiddle is built on trust. To keep buyers happy and your seller
            rating strong, we ask all sellers to follow these simple shipping
            guidelines:
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <ScheduleOutlinedIcon
                  fontSize="small"
                  sx={{ mt: "3px", color: "primary.main" }}
                />
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Ship within 72 hours
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Once an order comes in, packages should be dropped off and
                    handed to the carrier within 72 hours.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <LocalShippingOutlinedIcon
                  fontSize="small"
                  sx={{ mt: "3px", color: "primary.main" }}
                />
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Add tracking from your account
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    After you ship, add your tracking number directly from your
                    Account page so the buyer can follow the package.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <EmailOutlinedIcon
                  fontSize="small"
                  sx={{ mt: "3px", color: "primary.main" }}
                />
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Reach out if needed
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    You&apos;ll see the buyer&apos;s email on the order so you
                    can contact them directly if there&apos;s a delay or
                    question.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Payout timeline + testimonial */}
        <Grid container spacing={4} mb={4}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                backgroundColor:
                  theme.palette.mode === "light"
                    ? "rgba(255, 143, 66, 0.04)"
                    : "rgba(255, 255, 255, 0.04)",
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                💵 Payout timeline
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>First payout:</strong> usually 7–14 days after your
                first completed sale. This helps Stripe prevent fraud.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>After that:</strong> deposits are processed on a rolling
                basis about every 2 days, straight to your bank.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor:
                  theme.palette.mode === "light"
                    ? "#ffffff"
                    : "rgba(255,255,255,0.03)",
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                What other sellers say
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                &quot;I listed my first toy in minutes and the payout just
                showed up in my bank. Way simpler than dealing with auction
                sites.&quot;
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: "block", mt: 1 }}
              >
                — FiddlePiddle seller
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Quick FAQ using Accordions */}
        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Quick FAQs
          </Typography>

          <Accordion
            expanded={expandedFaq === "panel1"}
            onChange={handleFaqChange("panel1")}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight="bold">
                Why do I have to complete this setup?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                This setup connects your FiddlePiddle account to Stripe so you
                can get paid safely and on time. It also helps us verify that
                every seller is real, which protects both buyers and sellers.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedFaq === "panel2"}
            onChange={handleFaqChange("panel2")}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight="bold">
                What information will I need?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                Expect to share basic personal details, where you live, and
                where you want payouts sent. In some cases, Stripe may ask for
                an ID or extra proof just once.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedFaq === "panel3"}
            onChange={handleFaqChange("panel3")}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight="bold">
                How long does it take?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                Most sellers finish in just a few minutes. Once you&apos;re
                done, you can immediately start creating listings and posting
                toys for sale.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* CTA area - stacked to work with full-width FormActionButton */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 0.5 }}
          >
            No fees for setup — we only take a 7.5% commission when an item
            sells.
          </Typography>

          <Link
            component="button"
            type="button"
            onClick={handleBrowseInstead}
            sx={{
              fontSize: 12,
              textTransform: "none",
              p: 0,
              minWidth: "auto",
            }}
          >
            Not ready yet? Browse toys instead
          </Link>

          <Box mt={2} display="flex" justifyContent="center">
            <Box sx={{ width: "100%", maxWidth: "500px" }}>
              <Box mb={2} display="flex" alignItems="flex-start">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  style={{ marginTop: 4, marginRight: 8 }}
                />
                <label
                  htmlFor="agree-terms"
                  style={{
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  I agree to FiddlePiddle’s{" "}
                  <a
                    href="/blog/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Seller Terms
                  </a>{" "}
                  and understand that identity and payout verification is
                  required to sell.
                </label>
              </Box>

              <FormActionButton
                text="Start seller setup"
                onClick={handleStartSetup}
                disabled={!agreedToTerms}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Presell;
